import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import moment from "moment";
import { toast } from "react-toastify";
import { getPmSessionMonthRangeStrings } from "helpers/pmSessionDateHelper";
import { getCptAnalyticsTimezone } from "../common/cptAnalyticsTimezone";
import MonthFilter from "./MonthFilter";
import MonthlyPhoneReportNav from "./MonthlyPhoneReportNav";
import PatientOutreachReport from "./PatientOutreachReport";
import TeamPerformanceReport from "./TeamPerformanceReport";
import MonthlyPhoneNotesModal from "./MonthlyPhoneNotesModal";
import TeamMemberConsultationsModal from "./TeamMemberConsultationsModal";
import MonthlyPhoneConsultationShimmer from "./MonthlyPhoneConsultationShimmer";
import ProgramTypeSelector from "./ProgramTypeSelector";
import CptDashboardSkeletonOverlay from "../common/CptDashboardSkeletonOverlay";
import { scrollCptDashboardToTop } from "../common/cptDashboardScroll";
import {
  getMpcCopy,
  MONTHLY_PHONE_CONSULTATION_PROGRAMS,
  normalizeProgramType
} from "./monthlyPhoneConsultationLabels";
import "../PatientCPTRequirements/ReportComponents.css";
import "./MonthlyPhoneConsultationDashboard.css";

const getDefaultFilters = () => ({
  month: moment().format("MM"),
  year: moment().format("YYYY")
});

const MonthlyPhoneConsultationDashboard = ({
  dispatch,
  dashboardData,
  loading,
  teamMemberDetail,
  teamMemberDetailLoading,
  isActiveTab = true
}) => {
  const [filters, setFilters] = useState(getDefaultFilters());
  const [programType, setProgramType] = useState(null);
  const [activeReportTab, setActiveReportTab] = useState("patientOutreach");
  const mpcCopy = useMemo(() => getMpcCopy(programType), [programType]);
  const selectedProgram = MONTHLY_PHONE_CONSULTATION_PROGRAMS[normalizeProgramType(programType)];

  const handleReportTabChange = useCallback((tabId) => {
    setActiveReportTab(tabId);
    requestAnimationFrame(() => {
      scrollCptDashboardToTop(".mpc-report-subnav-shell");
    });
  }, []);
  const [teamMemberModalOpen, setTeamMemberModalOpen] = useState(false);
  const [notesModalPatient, setNotesModalPatient] = useState(null);
  const [notesModalConfig, setNotesModalConfig] = useState({
    notes: [],
    titlePrefix: getMpcCopy("rpm").consultationModalTitle,
    emptyMessage: getMpcCopy("rpm").consultationEmptyMessage,
    tone: "neutral"
  });

  const fetchDashboard = (nextFilters, nextProgramType = programType) => {
    if (!nextProgramType) {
      return;
    }

    const appliedFilters = nextFilters || filters;
    const range = getPmSessionMonthRangeStrings(appliedFilters.year, appliedFilters.month);
    dispatch({
      type: "DO_GET_MONTHLY_PHONE_CONSULTATION_DASHBOARD",
      payload: {
        startDate: range.startDate,
        endDate: range.endDate,
        timezone: getCptAnalyticsTimezone(),
        programType: normalizeProgramType(nextProgramType)
      }
    });
  };

  useEffect(() => {
    if (!isActiveTab || !programType) {
      return;
    }
    fetchDashboard(filters, programType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveTab, programType]);

  const handleProgramSelect = (nextProgramType) => {
    const normalizedProgramType = normalizeProgramType(nextProgramType);
    setProgramType(normalizedProgramType);
    setActiveReportTab("patientOutreach");
    dispatch({ type: "RESET_MONTHLY_PHONE_CONSULTATION_DASHBOARD" });
    dispatch({ type: "RESET_MONTHLY_PHONE_TEAM_MEMBER_DETAIL" });
    requestAnimationFrame(() => {
      scrollCptDashboardToTop(".mpc-dashboard");
    });
  };

  const handleProgramChange = (nextProgramType) => {
    if (normalizeProgramType(nextProgramType) === normalizeProgramType(programType)) {
      return;
    }
    handleProgramSelect(nextProgramType);
  };

  const haveMet = dashboardData?.haveMet || [];
  const haveNotMet = dashboardData?.haveNotMet || [];
  const totalPatientsCount = haveMet.length + haveNotMet.length;
  const monthLabel = dashboardData?.monthLabel || moment(`${filters.year}-${filters.month}-01`).format("MMMM YYYY");
  const staffPerformance = dashboardData?.staffPerformance;
  const teamMemberCount = staffPerformance?.members?.length ?? 0;
  const isInitialLoad = loading && !dashboardData;

  const getPatientDashboardPath = (patient = {}) => {
    const patientId = patient._id;
    if (!patientId) return "";
    const name = patient.patientName || "";
    const parts = String(name).includes(",") ? name.split(",").map((p) => p.trim()) : name.split(/\s+/);
    const lastName = parts[0] || "";
    const firstName = parts[1] || parts[0] || "";
    const slug = `${firstName}-${lastName}`.replace(/\s+/g, "-").replace(/[^a-zA-Z0-9-]/g, "").toLowerCase();
    return `/dashboard/${slug || "patient"}/${patientId}`;
  };

  const handleViewPatient = (patient) => {
    const path = getPatientDashboardPath(patient);
    if (!path) {
      toast.error("Patient profile is unavailable.");
      return;
    }
    window.location.href = path;
  };

  const handleViewNotes = (patient) => {
    const notes = patient?.monthlyPhoneNotes || [];
    if (!notes.length) {
      toast.info(mpcCopy.consultationEmptyPatientMessage);
      return;
    }
    setNotesModalConfig({
      notes,
      titlePrefix: "Consultation activity",
      emptyMessage: mpcCopy.consultationEmptyMessage,
      tone: "neutral"
    });
    setNotesModalPatient(patient);
  };

  const handleViewFailedAttempts = (patient) => {
    if (!patient?.failedPhoneNotes?.length) {
      toast.info(mpcCopy.failedAttemptsEmptyPatientMessage);
      return;
    }
    setNotesModalConfig({
      notes: patient.failedPhoneNotes,
      titlePrefix: mpcCopy.failedAttemptsModalTitle,
      emptyMessage: mpcCopy.failedAttemptsEmptyMessage,
      tone: "failed"
    });
    setNotesModalPatient(patient);
  };

  const handleViewSuccessfulConsultations = (patient) => {
    if (!patient?.successfulConsultationNotes?.length) {
      toast.info(mpcCopy.consultationEmptyPatientMessage);
      return;
    }
    setNotesModalConfig({
      notes: patient.successfulConsultationNotes,
      titlePrefix: mpcCopy.consultationModalTitle,
      emptyMessage: mpcCopy.consultationEmptyMessage,
      tone: "success"
    });
    setNotesModalPatient(patient);
  };

  const closeNotesModal = () => {
    setNotesModalPatient(null);
    setNotesModalConfig({
      notes: [],
      titlePrefix: mpcCopy.consultationModalTitle,
      emptyMessage: mpcCopy.consultationEmptyMessage,
      tone: "neutral"
    });
  };

  const closeTeamMemberModal = () => {
    setTeamMemberModalOpen(false);
    dispatch({ type: "RESET_MONTHLY_PHONE_TEAM_MEMBER_DETAIL" });
  };

  const handleTeamMemberMonthClick = ({ teamMemberUserId, startDate, endDate }) => {
    if (!teamMemberUserId || !startDate || !endDate) {
      toast.error("Unable to load consultation details for this month.");
      return;
    }

    setTeamMemberModalOpen(true);
    dispatch({
      type: "DO_GET_MONTHLY_PHONE_TEAM_MEMBER_DETAIL",
      payload: {
        teamMemberUserId,
        startDate,
        endDate,
        timezone: getCptAnalyticsTimezone(),
        programType: normalizeProgramType(programType)
      }
    });
  };

  if (!isActiveTab) {
    return null;
  }

  if (!programType) {
    return (
      <div className="report-container mpc-dashboard cpt-dash-report-surface">
        <ProgramTypeSelector onSelect={handleProgramSelect} />
      </div>
    );
  }

  return (
    <div
      className={`report-container mpc-dashboard cpt-dash-report-surface ${
        !isInitialLoad && loading ? "cpt-dash-report-surface--busy" : ""
      }`}
    >
      <MonthFilter
        filters={filters}
        onChange={setFilters}
        onApply={fetchDashboard}
        loading={loading}
        activeReportTab={activeReportTab}
        programType={programType}
        selectedProgram={selectedProgram}
        onProgramChange={handleProgramChange}
      />

      {isInitialLoad ? (
        <MonthlyPhoneConsultationShimmer variant={activeReportTab} />
      ) : (
        <>
          {!isInitialLoad && loading && (
            <CptDashboardSkeletonOverlay>
              <MonthlyPhoneConsultationShimmer variant={activeReportTab} />
            </CptDashboardSkeletonOverlay>
          )}
          <div className="mpc-content">
            <MonthlyPhoneReportNav
              activeReportTab={activeReportTab}
              onChange={handleReportTabChange}
              counts={{
                patientOutreach: totalPatientsCount,
                teamPerformance: teamMemberCount
              }}
            />

            {activeReportTab === "patientOutreach" && (
              <PatientOutreachReport
                haveMet={haveMet}
                haveNotMet={haveNotMet}
                filters={filters}
                programType={programType}
                mpcCopy={mpcCopy}
                onViewPatient={handleViewPatient}
                onViewNotes={handleViewNotes}
                onViewFailedAttempts={handleViewFailedAttempts}
                onViewSuccessfulConsultations={handleViewSuccessfulConsultations}
              />
            )}

            {activeReportTab === "teamPerformance" && (
              <TeamPerformanceReport
                staffPerformance={staffPerformance}
                monthLabel={monthLabel}
                programType={programType}
                mpcCopy={mpcCopy}
                onTeamMemberMonthClick={handleTeamMemberMonthClick}
              />
            )}
          </div>
        </>
      )}

      <MonthlyPhoneNotesModal
        isOpen={Boolean(notesModalPatient)}
        toggle={closeNotesModal}
        patient={notesModalPatient}
        notes={notesModalConfig.notes}
        titlePrefix={notesModalConfig.titlePrefix}
        emptyMessage={notesModalConfig.emptyMessage}
        tone={notesModalConfig.tone}
        consultationNoteTitle={selectedProgram && selectedProgram.title}
      />

      <TeamMemberConsultationsModal
        isOpen={teamMemberModalOpen}
        toggle={closeTeamMemberModal}
        detail={teamMemberDetail}
        loading={teamMemberDetailLoading}
        programType={programType}
        mpcCopy={mpcCopy}
        onViewPatient={handleViewPatient}
      />
    </div>
  );
};

const mapStateToProps = ({ careprovider }) => ({
  dashboardData: careprovider.monthlyPhoneConsultationDashboardData,
  loading: careprovider.ongoingMonthlyPhoneConsultationDashboardRequest,
  teamMemberDetail: careprovider.monthlyPhoneTeamMemberDetailData,
  teamMemberDetailLoading: careprovider.ongoingMonthlyPhoneTeamMemberDetailRequest
});

export default connect(mapStateToProps)(MonthlyPhoneConsultationDashboard);
